import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FileTextIcon, PlusIcon, SearchIcon, TagIcon, CalendarIcon, UserIcon, PencilIcon, TrashIcon, CheckIcon, XIcon } from 'lucide-react';
// Mock data for instructor notes
const mockNotes = [{
  id: 1,
  title: 'Aandachtspunten Sliptraining Basis',
  content: 'Bij de oefening "noodstop" moet extra aandacht worden besteed aan het niet blokkeren van de wielen. Veel deelnemers hebben moeite met het gedoseerd remmen.',
  date: new Date(new Date().setDate(new Date().getDate() - 5)),
  tags: ['sliptraining', 'oefeningen', 'remmen'],
  trainingType: 'Sliptraining Basis'
}, {
  id: 2,
  title: 'Feedback van deelnemers Defensief Rijden',
  content: 'Deelnemers gaven aan dat ze graag meer praktijkvoorbeelden willen zien van anticiperend rijden in stadsverkeer. Voor de volgende training meer video-materiaal voorbereiden.',
  date: new Date(new Date().setDate(new Date().getDate() - 10)),
  tags: ['defensief rijden', 'feedback', 'verbetering'],
  trainingType: 'Defensief Rijden'
}, {
  id: 3,
  title: 'Nieuwe oefening voor Code 95',
  content: 'Idee voor nieuwe oefening: laat deelnemers in groepjes van 3 een route plannen waarbij ze rekening moeten houden met rij- en rusttijden, verkeersintensiteit en brandstofverbruik. Daarna plenair bespreken.',
  date: new Date(new Date().setDate(new Date().getDate() - 15)),
  tags: ['code 95', 'oefening', 'idee'],
  trainingType: 'Code 95 Training'
}, {
  id: 4,
  title: 'Materiaal check voor slipbaan Rotterdam',
  content: 'Bij de laatste training in Rotterdam waren er problemen met de pylonen (te weinig en sommige beschadigd). Voor de volgende training extra materiaal meenemen en vooraf checken.',
  date: new Date(new Date().setDate(new Date().getDate() - 20)),
  tags: ['materiaal', 'rotterdam', 'voorbereiding'],
  trainingType: 'Sliptraining Gevorderd'
}, {
  id: 5,
  title: 'Evaluatie calamiteitentraining brandweer',
  content: 'De calamiteitentraining voor de brandweer was zeer succesvol. Specifieke feedback: meer aandacht besteden aan het rijden met zwaailicht in woonwijken. Ook was er vraag naar een vervolgtraining specifiek gericht op het rijden met grotere voertuigen.',
  date: new Date(new Date().setDate(new Date().getDate() - 25)),
  tags: ['calamiteiten', 'brandweer', 'evaluatie', 'vervolgtraining'],
  trainingType: 'Calamiteitentraining'
}];
// All available training types
const trainingTypes = ['Sliptraining Basis', 'Sliptraining Gevorderd', 'Defensief Rijden', 'Code 95 Training', 'Calamiteitentraining'];
// Helper function to format date
const formatDate = date => {
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};
export function MyNotes() {
  const [notes, setNotes] = useState(mockNotes);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedTrainingType, setSelectedTrainingType] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Get all unique tags from notes
  const getAllTags = () => {
    const tags = new Set();
    notes.forEach(note => {
      note.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  };
  // Filter notes based on search term, selected tag, and training type
  const getFilteredNotes = () => {
    return notes.filter(note => {
      const matchesSearch = searchTerm === '' || note.title.toLowerCase().includes(searchTerm.toLowerCase()) || note.content.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag = selectedTag === '' || note.tags.includes(selectedTag);
      const matchesTrainingType = selectedTrainingType === '' || note.trainingType === selectedTrainingType;
      return matchesSearch && matchesTag && matchesTrainingType;
    });
  };
  // Handle creating/editing note
  const handleCreateOrEditNote = () => {
    if (editingNote.id) {
      // Update existing note
      setNotes(notes.map(note => note.id === editingNote.id ? editingNote : note));
      setSuccessMessage('Notitie succesvol bijgewerkt');
    } else {
      // Create new note
      const newNote = {
        ...editingNote,
        id: Date.now(),
        date: new Date()
      };
      setNotes([newNote, ...notes]);
      setSuccessMessage('Notitie succesvol aangemaakt');
    }
    setShowNoteModal(false);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Handle delete note
  const handleDeleteNote = () => {
    setNotes(notes.filter(note => note.id !== noteToDelete));
    setShowDeleteConfirm(false);
    setNoteToDelete(null);
    setSuccessMessage('Notitie succesvol verwijderd');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Open create note modal
  const openCreateNoteModal = () => {
    setEditingNote({
      id: null,
      title: '',
      content: '',
      tags: [],
      trainingType: ''
    });
    setShowNoteModal(true);
  };
  // Open edit note modal
  const openEditNoteModal = note => {
    setEditingNote({
      ...note
    });
    setShowNoteModal(true);
  };
  // Open delete confirmation
  const openDeleteConfirm = noteId => {
    setNoteToDelete(noteId);
    setShowDeleteConfirm(true);
  };
  // Handle tag input
  const handleTagInput = e => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      if (!editingNote.tags.includes(e.target.value.trim())) {
        setEditingNote({
          ...editingNote,
          tags: [...editingNote.tags, e.target.value.trim()]
        });
      }
      e.target.value = '';
    }
  };
  // Remove tag from editing note
  const removeTag = tagToRemove => {
    setEditingNote({
      ...editingNote,
      tags: editingNote.tags.filter(tag => tag !== tagToRemove)
    });
  };
  // Get filtered notes
  const filteredNotes = getFilteredNotes();
  return <div className="space-y-6" data-id="element-1400">
      <div className="flex justify-between items-center" data-id="element-1401">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1402">
          Mijn Notities & Inzichten
        </h1>
        <Button variant="primary" size="sm" onClick={openCreateNoteModal} data-id="element-1403">
          <PlusIcon className="h-4 w-4 mr-1" data-id="element-1404" />
          Nieuwe Notitie
        </Button>
      </div>

      {/* Success message */}
      {showSuccessMessage && <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 shadow-md flex items-center" data-id="element-1405">
          <CheckIcon className="h-5 w-5 mr-2" data-id="element-1406" />
          <span data-id="element-1407">{successMessage}</span>
        </div>}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1408">
        <div className="col-span-1" data-id="element-1409">
          <div className="relative" data-id="element-1410">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1411">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1412" />
            </div>
            <input type="text" placeholder="Zoek in notities..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1413" />
          </div>
        </div>
        <div className="col-span-1" data-id="element-1414">
          <div className="relative" data-id="element-1415">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1416">
              <TagIcon className="h-5 w-5 text-gray-400" data-id="element-1417" />
            </div>
            <select className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full" value={selectedTag} onChange={e => setSelectedTag(e.target.value)} data-id="element-1418">
              <option value="" data-id="element-1419">Alle tags</option>
              {getAllTags().map(tag => <option key={tag} value={tag} data-id="element-1420">
                  {tag}
                </option>)}
            </select>
          </div>
        </div>
        <div className="col-span-1" data-id="element-1421">
          <div className="relative" data-id="element-1422">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1423">
              <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-1424" />
            </div>
            <select className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full" value={selectedTrainingType} onChange={e => setSelectedTrainingType(e.target.value)} data-id="element-1425">
              <option value="" data-id="element-1426">Alle trainingstypes</option>
              {trainingTypes.map(type => <option key={type} value={type} data-id="element-1427">
                  {type}
                </option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Notes list */}
      {filteredNotes.length > 0 ? <div className="space-y-4" data-id="element-1428">
          {filteredNotes.map(note => <Card key={note.id} data-id="element-1429">
              <div className="p-4" data-id="element-1430">
                <div className="flex justify-between items-start" data-id="element-1431">
                  <div data-id="element-1432">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-1433">
                      {note.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-1434">
                      <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1435" />
                      <span data-id="element-1436">{formatDate(new Date(note.date))}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-1437">
                    <button className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100" onClick={() => openEditNoteModal(note)} title="Bewerken" data-id="element-1438">
                      <PencilIcon className="h-5 w-5" data-id="element-1439" />
                    </button>
                    <button className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-gray-100" onClick={() => openDeleteConfirm(note.id)} title="Verwijderen" data-id="element-1440">
                      <TrashIcon className="h-5 w-5" data-id="element-1441" />
                    </button>
                  </div>
                </div>
                <div className="mt-2" data-id="element-1442">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1443">
                    {note.trainingType}
                  </span>
                </div>
                <p className="mt-3 text-gray-600 whitespace-pre-line" data-id="element-1444">
                  {note.content}
                </p>
                <div className="mt-3 flex flex-wrap gap-1" data-id="element-1445">
                  {note.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 cursor-pointer hover:bg-gray-200" onClick={() => setSelectedTag(tag)} data-id="element-1446">
                      #{tag}
                    </span>)}
                </div>
              </div>
            </Card>)}
        </div> : <Card data-id="element-1447">
          <div className="p-6 text-center" data-id="element-1448">
            <FileTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1449" />
            <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1450">
              Geen notities gevonden
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-1451">
              {searchTerm || selectedTag || selectedTrainingType ? 'Er zijn geen notities die overeenkomen met je zoekcriteria.' : 'Je hebt nog geen notities aangemaakt.'}
            </p>
            <Button variant="outline" onClick={openCreateNoteModal} data-id="element-1452">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-1453" />
              Nieuwe Notitie
            </Button>
          </div>
        </Card>}

      {/* Create/Edit Note Modal */}
      {showNoteModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1454">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" data-id="element-1455">
            <div className="p-4 border-b border-gray-200" data-id="element-1456">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1457">
                {editingNote.id ? 'Notitie Bewerken' : 'Nieuwe Notitie'}
              </h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-1458">
              <div data-id="element-1459">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1460">
                  Titel
                </label>
                <input type="text" id="title" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Titel van je notitie" value={editingNote.title} onChange={e => setEditingNote({
              ...editingNote,
              title: e.target.value
            })} data-id="element-1461" />
              </div>
              <div data-id="element-1462">
                <label htmlFor="trainingType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1463">
                  Training Type
                </label>
                <select id="trainingType" className="w-full p-2 border border-gray-300 rounded-md" value={editingNote.trainingType} onChange={e => setEditingNote({
              ...editingNote,
              trainingType: e.target.value
            })} data-id="element-1464">
                  <option value="" data-id="element-1465">Selecteer een type</option>
                  {trainingTypes.map(type => <option key={type} value={type} data-id="element-1466">
                      {type}
                    </option>)}
                </select>
              </div>
              <div data-id="element-1467">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1468">
                  Inhoud
                </label>
                <textarea id="content" rows={6} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Schrijf hier je notitie..." value={editingNote.content} onChange={e => setEditingNote({
              ...editingNote,
              content: e.target.value
            })} data-id="element-1469"></textarea>
              </div>
              <div data-id="element-1470">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1471">
                  Tags
                </label>
                <div className="flex flex-wrap gap-1 mb-2" data-id="element-1472">
                  {editingNote.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1473">
                      #{tag}
                      <button type="button" className="ml-1 text-gray-400 hover:text-gray-600" onClick={() => removeTag(tag)} data-id="element-1474">
                        <XIcon className="h-3 w-3" data-id="element-1475" />
                      </button>
                    </span>)}
                </div>
                <input type="text" id="tags" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Typ een tag en druk op Enter..." onKeyDown={handleTagInput} data-id="element-1476" />
              </div>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-1477">
              <Button variant="outline" onClick={() => setShowNoteModal(false)} data-id="element-1478">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleCreateOrEditNote} disabled={!editingNote.title || !editingNote.content || !editingNote.trainingType} data-id="element-1479">
                {editingNote.id ? 'Bijwerken' : 'Aanmaken'}
              </Button>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1480">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-1481">
            <div className="p-4 border-b border-gray-200" data-id="element-1482">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1483">
                Notitie Verwijderen
              </h3>
            </div>
            <div className="p-4" data-id="element-1484">
              <p className="text-gray-600" data-id="element-1485">
                Weet je zeker dat je deze notitie wilt verwijderen? Deze actie
                kan niet ongedaan worden gemaakt.
              </p>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-1486">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-id="element-1487">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleDeleteNote} className="bg-red-600 hover:bg-red-700" data-id="element-1488">
                <TrashIcon className="h-4 w-4 mr-1" data-id="element-1489" />
                Verwijderen
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}